import { useState } from 'react';
import { useAdminAuth } from '../auth/AdminAuthContext';
import { Navigate } from 'react-router-dom';

import DownloadsList from './DownloadsList';
import DownloadNew from './DownloadNew';
import EmailUpdate from './EmailUpdate';
import SearchUsers from './SearchUsers';
import EditUsers from './EditUsers';
import AddUsers from './AddUsers';
import EmailGroups from './EmailGroups';


import { LoadingAnimatedPage } from '../components/Loading';
import UserActions from './UsersActions';
import EmailNewsletter from './EmailNewsletter';

interface EditUserData {
  id: string;
  email: string;
}

interface Tab {
  id: string;
  label: string;
  content: React.ReactNode;
}

function Tabbed({ tabs }: { tabs: Tab[] }) {
  const [activeTab, setActiveTab] = useState<string>(tabs[0]?.id || '');

  return (
    <div className="h-full flex flex-col">
      <div className="flex border-b border-light-border">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 border-r border-light-border hover:bg-light-main-hover hover:text-light-text-alt cursor-pointer duration-200 ease-in-out ${
              activeTab === tab.id ? 'bg-light-main text-light-text-alt' : ''
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 p-4 overflow-y-auto">
        {tabs.find((tab) => tab.id === activeTab)?.content}
      </div>
    </div>
  );
}

function UsersMenu() {
  const [activeTab, setActiveTab] = useState<string>('findusers');
  const [editUserData, setEditUserData] = useState<EditUserData | null>(null);

  const userTabs: Tab[] = [
    {
      id: 'findusers',
      label: 'Find Users',
      content: (
        <SearchUsers
          onEditUser={(data) => {
            setEditUserData(data);
            setActiveTab('edit');
          }}
          onActionsUser={(data) => {
            setEditUserData(data);
            setActiveTab('actionsusers')
          }}

        />
      )
    },
    { id: 'addusers', label: 'Add Users', content: <AddUsers /> },
    {
      id: 'edit',
      label: 'Edit Users',
      content: (
        <EditUsers
          initialUserData={editUserData}
          onUserUpdated={() => setEditUserData(null)}
        />
      )
    },
    {
      id: 'actionsusers',
      label: 'User Actions',
      content: <UserActions initialUserData={editUserData} />
    }
  ];

  return (
    <div className="h-full flex flex-col">
      <div className="flex border-b border-light-border">
        {userTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-4 py-2 border-r border-light-border hover:bg-light-main-hover hover:text-light-text-alt cursor-pointer duration-200 ease-in-out ${
              activeTab === tab.id ? 'bg-light-main text-light-text-alt' : ''
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      <div className="flex-1 p-4 overflow-y-auto">
        {userTabs.find((tab) => tab.id === activeTab)?.content}
      </div>
    </div>
  );
}

const DownloadsMenu = () => <Tabbed tabs={downloadsTabs} />;
const EmailsMenu = () => <Tabbed tabs={emailsTabs} />;

const downloadsTabs: Tab[] = [
  { id: 'view', label: 'Download List', content: <DownloadsList /> },
  {
    id: 'upload',
    label: 'Upload New',
    content: <DownloadNew />
  }
];

const emailsTabs: Tab[] = [

  {
    id: 'terrastationupdates',
    label: 'TerraStation Updates',
    content: <EmailUpdate />
  },
  {
    id: 'emailgroups',
    label: 'Email Groups',
    content: <EmailGroups />
  }, {
    id: 'newsletter',
    label: 'Newsletter Emails',
    content: <EmailNewsletter />
  }

];

const menuItems = [
  { id: 'users', label: 'Users', Component: UsersMenu },
  { id: 'downloads', label: 'Downloads', Component: DownloadsMenu },
  { id: 'emails', label: 'Emails', Component: EmailsMenu }
];

function Admin() {
  const { user, roles, loading } = useAdminAuth();
  const [activeMenu, setActiveMenu] = useState<string>('users');

  if (loading) return <LoadingAnimatedPage />;
  if (!roles?.includes('groupAdministrator')) return <Navigate to="/login" />;
  return (
    <div id="admin-wrapper" className="relative flex w-full h-screen">
      <div
        id="admin-align-v"
        className="relative w-full h-full grid grid-rows-[calc(100vh/14)_1fr]"
      >
        <div
          id="admin-header"
          className="flex flex-row px-4 justify-center w-full h-full items-center border-b border-light-border"
        >
          <div
            id="admin-header-align-h"
            className="w-full h-full items-center justify-center grid grid-cols-[20%_25%_25%_1fr]"
          >
            <div className="border-r border-light-border mr-4 h-full flex items-center">
              <b>Admin Panel</b>
            </div>
            <div className="border-r border-light-border mr-4 h-full flex items-center">
              <b>User Email:</b>
              <span className="ml-2">{user?.email}</span>
            </div>
            <div className="border-r border-light-border mr-4 h-full flex items-center">
              <b>Roles:</b>
              <span className="ml-2">{roles.join(', ')}</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-[200px_1fr] h-full">
          <div className="border-r border-light-border overflow-y-auto">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => setActiveMenu(item.id)}
                className={`w-full cursor-pointer duration-200 ease-in-out text-left p-4 border-b border-light-border hover:bg-light-main-hover hover:text-light-text-alt ${
                  activeMenu === item.id
                    ? 'bg-light-main text-light-text-alt'
                    : ''
                }`}
              >
                {item.label}
              </button>
            ))}
          </div>
          <div className="h-full w-full overflow-y-auto">
            {activeMenu &&
              (() => {
                const item = menuItems.find((i) => i.id === activeMenu);
                return item ? <item.Component /> : null;
              })()}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Admin;
